import { useState } from "react";
import {
  Modal,
  Button,
  Tabs,
  Tab,
  Table,
  Alert,
  Row,
  Col,
  Card,
  Badge,
  Pagination,
} from "react-bootstrap";

function CajaDetailsModal({ show, handleClose, caja }) {
  // Estado para controlar la pestaña activa
  const [activeTab, setActiveTab] = useState("saldos");

  // Estados para controlar la paginación (6 elementos por página)
  const [currentPageSaldos, setCurrentPageSaldos] = useState(1);
  const [currentPageQr, setCurrentPageQr] = useState(1);
  const ITEMS_PER_PAGE = 3;

  if (!caja) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(amount || 0);
  };

  const formatCash = (cash) => {
    if (typeof cash === "number") {
      return cash.toFixed(2);
    }
    return "0.00";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = String(date.getUTCFullYear()).slice(-2);
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const qrOperations = Array.isArray(caja.qr) ? caja.qr : [];
  const saldosVendidos = Array.isArray(caja.box_saldo) ? caja.box_saldo : [];
  const totalAccepted = qrOperations
    .filter((qr) => qr.state === "APPROVED")
    .reduce((acc, qr) => acc + (Number(qr.amount) || 0), 0);

  const totalRejected = qrOperations
    .filter((qr) => qr.state === "REJECTED")
    .reduce((acc, qr) => acc + (Number(qr.amount) || 0), 0);

  const totalBalance = saldosVendidos.reduce((acc, venta) => {
    const monto = Number(venta.balance) || 0;

    if (venta.types === "Retiro de dinero") {
      return acc - monto;
    }

    return acc + monto;
  }, 0);

  // --- LÓGICA DE PAGINACIÓN: SALDOS VENDIDOS ---
  const totalPagesSaldos = Math.ceil(saldosVendidos.length / ITEMS_PER_PAGE);
  const currentSaldos = saldosVendidos.slice(
    (currentPageSaldos - 1) * ITEMS_PER_PAGE,
    currentPageSaldos * ITEMS_PER_PAGE
  );

  // --- LÓGICA DE PAGINACIÓN: OPERACIONES QR ---
  const totalPagesQr = Math.ceil(qrOperations.length / ITEMS_PER_PAGE);
  const currentQr = qrOperations.slice(
    (currentPageQr - 1) * ITEMS_PER_PAGE,
    currentPageQr * ITEMS_PER_PAGE
  );

  // Función auxiliar para renderizar los botones de paginación
  const renderPagination = (currentPage, totalPages, onPageChange) => {
    if (totalPages <= 1) return null;

    let items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => onPageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-3">
        <Pagination size="sm" className="mb-0">
          <Pagination.Prev
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
          />
          {items}
          <Pagination.Next
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          />
        </Pagination>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title style={{ color: "#fd6b0c", fontWeight: "bold" }}>
          Detalles de la Caja{" "}
          <span className="text-muted fs-6">(ID: {caja.idBox})</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {/* SECCIÓN DE INFORMACIÓN PRINCIPAL */}
        <Row className="g-3 mb-4">
          {/* Tarjeta Empleado */}
          <Col md={4}>
            <Card className="h-100 border-0 bg-light shadow-sm">
              <Card.Body>
                <h6 className="fw-bold mb-3" style={{ color: "#fd6b0c" }}>
                  Información del Empleado
                </h6>
                <div className="small">
                  <div className="mb-1">
                    <strong>Nombre:</strong> {caja.empleado?.name}{" "}
                    {caja.empleado?.lastName}
                  </div>
                  <div className="mb-1">
                    <strong>CUIT:</strong> {caja.empleado?.cuit || "-"}
                  </div>
                  <div>
                    <strong>Email:</strong> {caja.empleado?.email || "-"}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Tarjeta Comercio */}
          <Col md={4}>
            <Card className="h-100 border-0 bg-light shadow-sm">
              <Card.Body>
                <h6 className="text-success fw-bold mb-3">
                  Detalles del Comercio
                </h6>
                <div className="small">
                  <div className="mb-1">
                    <strong>Comercio:</strong> {caja.comercio?.nameShop || "-"}
                  </div>
                  <div className="mb-1">
                    <strong>CUIT:</strong> {caja.comercio?.cuit || "-"}
                  </div>
                  <div className="mb-1">
                    <strong>CVU:</strong> {caja.comercio?.cvu || "-"}
                  </div>
                  <div>
                    <strong>Sucursal:</strong>{" "}
                    {caja.comercio?.branches?.name || "-"}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Tarjeta Métricas QR */}
          <Col md={4}>
            <Card className="h-100 border-0 bg-light shadow-sm">
              <Card.Body className="d-flex flex-column justify-content-center">
                <h6 className="text-secondary fw-bold mb-3">Totales</h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small font-weight-bold">Balance:</span>
                  <span className="text-success fw-bold fs-6">
                    {formatCurrency(totalBalance)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small font-weight-bold">
                    Operaciones QR aceptadas:
                  </span>
                  <span className="text-success fw-bold fs-6">
                    {formatCurrency(totalAccepted)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small font-weight-bold">
                    Operaciones QR rechazadas:
                  </span>
                  <span className="text-danger fw-bold fs-6">
                    {formatCurrency(totalRejected)}
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* SECCIÓN DE PESTAÑAS (TABLAS CON PAGINACIÓN) */}
        <Tabs
          id="historial-caja-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
          fill
        >
          {/* Fichero 1: Saldos Vendidos */}
          <Tab
            eventKey="saldos"
            title={`Saldos Vendidos (${saldosVendidos.length})`}
          >
            {saldosVendidos.length === 0 ? (
              <Alert variant="info" className="mt-3 text-center">
                No hay registros de saldos vendidos.
              </Alert>
            ) : (
              <>
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  align="middle"
                  className="mt-3 align-middle"
                >
                  <thead className="table-light">
                    <tr>
                      <th className="text-end">Balance</th>
                      <th>CUIT</th>
                      <th>CVU</th>
                      <th>Fecha</th>
                      <th>Tipo de Movimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSaldos.map((mov, index) => (
                      <tr key={index}>
                        <td className="text-end fw-bold">
                          ${formatCash(mov.balance)}
                        </td>
                        <td>{mov.cuit || "-"}</td>
                        <td>{mov.cvu || "-"}</td>
                        <td>{formatDate(mov.timestamp)}</td>
                        <td>{mov.types || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Control de paginación de Saldos */}
                {renderPagination(
                  currentPageSaldos,
                  totalPagesSaldos,
                  setCurrentPageSaldos
                )}
              </>
            )}
          </Tab>

          {/* Fichero 2: Operaciones QR */}
          <Tab eventKey="qr" title={`Operaciones QR (${qrOperations.length})`}>
            {qrOperations.length === 0 ? (
              <Alert variant="info" className="mt-3 text-center">
                No hay registros de operaciones QR.
              </Alert>
            ) : (
              <>
                <Table
                  striped
                  bordered
                  hover
                  responsive
                  align="middle"
                  className="mt-3 align-middle"
                >
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo QR</th>
                      <th>Estado</th>
                      <th className="text-end">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentQr.map((op, index) => (
                      <tr key={index}>
                        <td>{formatDate(op.date)}</td>
                        <td>{op.typeQR || "-"}</td>
                        <td>{op.state || "-"}</td>
                        <td className="text-end fw-bold">
                          ${formatCash(op.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Control de paginación de Operaciones QR */}
                {renderPagination(
                  currentPageQr,
                  totalPagesQr,
                  setCurrentPageQr
                )}
              </>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="border-top-0">
        <Button variant="secondary" onClick={handleClose} className="px-4">
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CajaDetailsModal;
